import React, { useReducer, useCallback, useMemo, useEffect } from 'react';
import {
  toDate,
  getStartOfMonth,
  getNextMonth,
  getPrevMonth,
  isMonthEqual,
  toMonthLabel,
} from './utils.js';

const calendarReducer = (state, action) => {
  switch (action.type) {
    case 'CHANGE_MONTH':
      return {
        ...state,
        isLoading: true,
        currentDate: action.newDate,
        hasNextMonth: action.hasNextMonth,
        hasPreviousMonth: action.hasPreviousMonth,
      };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        statusSummary: action.statusSummary,
        statusActivities: action.statusActivities,
      };
    case 'FAILURE':
      return { ...state, isLoading: false };
    default:
      throw new Error();
  }
};

export default function useCalendarState({
  date,
  maxDateProp,
  minDateProp,
  fetchPath,
}) {
  const maxDate = useMemo(() => getStartOfMonth(maxDateProp), []);
  const minDate = useMemo(() => getStartOfMonth(minDateProp), []);
  const [calendarState, dispatch] = useReducer(calendarReducer, {
    currentDate: getStartOfMonth(date || new Date()),
    isLoading: true,
    statusActivities: {},
    statusSummary: null,
    hasNextMonth: getStartOfMonth(date || new Date()) < maxDate,
    hasPreviousMonth: getStartOfMonth(date || new Date()) > minDate,
  });

  const monthOptions = useMemo(() => {
    const result = [];

    for (
      let month = toDate(minDate);
      month <= maxDate;
      month.setMonth(month.getMonth() + 1)
    ) {
      result.push({
        text: toMonthLabel(month),
        value: month.getTime().toString(),
        key: month.getTime(),
      });
    }
    return result;
  }, []);

  const handleFetchedResult = useCallback((result) => {
    const summary =
      result.average > 0 && `Running Monthly Average: ${result.average}`;

    dispatch({
      type: 'FETCH_SUCCESS',
      statusActivities: result.history,
      statusSummary: summary,
    });
  }, []);

  const handleMonthChange = useCallback(
    (newDate) => {
      newDate = newDate instanceof Date ? newDate : toDate(newDate);

      if (isMonthEqual(calendarState.currentDate, newDate)) {
        return null;
      }

      dispatch({
        type: 'CHANGE_MONTH',
        newDate,
        hasPreviousMonth: newDate > minDate,
        hasNextMonth: newDate < maxDate,
      });
    },
    [calendarState.currentDate],
  );

  const handleNextClick = useCallback(() => {
    if (calendarState.hasNextMonth) {
      handleMonthChange(getNextMonth(calendarState.currentDate));
    }
  }, [calendarState.currentDate, calendarState.hasNextMonth]);

  const handlePreviousClick = useCallback(() => {
    if (calendarState.hasPreviousMonth) {
      handleMonthChange(getPrevMonth(calendarState.currentDate));
    }
  }, [calendarState.currentDate, calendarState.hasPreviousMonth]);

  const getDateActivities = useCallback(
    (requestedDate) => {
      const activities = calendarState.statusActivities[requestedDate] || {};
      const content = [];

      if (activities.total > 0) {
        for (const status in activities) {
          content.push(
            <div key={status}>
              {status}: {activities[status]}
              <br />
            </div>,
          );
        }
      }
      return content.length > 0 ? content : 'None';
    },
    [calendarState.statusActivities],
  );

  useEffect(() => {
    const query = encodeURI(calendarState.currentDate.toISOString());

    fetch(`${fetchPath}?start_date=${query}`)
      .then((result) => result.json())
      .then((result) => handleFetchedResult(result))
      .catch(() => dispatch({ type: 'FAILURE' }));
  }, [calendarState.currentDate]);

  return {
    calendarState,
    handleFetchedResult,
    handleMonthChange,
    handleNextClick,
    handlePreviousClick,
    getDateActivities,
    monthOptions,
  };
}
